import React, { useState, useEffect, useRef } from "react";
import { useToast, ToasterToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react";

interface ToastItemProps {
  toast: ToasterToast;
  onDismiss: (id: string) => void;
}

function SaasToastItem({ toast, onDismiss }: ToastItemProps) {
  const duration = toast.duration || 4500;
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef<number>(Date.now());
  const remainingTimeRef = useRef<number>(duration);

  // Auto-dismiss with pause on hover
  useEffect(() => {
    if (isHovered) return;

    startTimeRef.current = Date.now();
    const intervalTime = 30;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const currentRemaining = Math.max(0, remainingTimeRef.current - elapsed);
      const pct = (currentRemaining / duration) * 100;
      setProgress(pct);

      if (currentRemaining <= 0) {
        clearInterval(interval);
        onDismiss(toast.id);
      }
    }, intervalTime);

    return () => {
      clearInterval(interval);
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    };
  }, [isHovered, toast.id, duration, onDismiss]);

  // Determine variant styling and icon
  const variant = toast.variant || "default";

  // Smart detect if title implies success or warning
  const titleStr = typeof toast.title === "string" ? toast.title.toLowerCase() : "";
  const isImplicitSuccess =
    variant === "default" &&
    (titleStr.includes("success") ||
      titleStr.includes("saved") ||
      titleStr.includes("created") ||
      titleStr.includes("approved") ||
      titleStr.includes("reconciled") ||
      titleStr.includes("restored"));

  const isImplicitWarning =
    variant === "default" &&
    (titleStr.includes("warning") || titleStr.includes("attention") || titleStr.includes("low stock"));

  const effectiveVariant =
    variant === "destructive"
      ? "destructive"
      : variant === "warning" || isImplicitWarning
      ? "warning"
      : variant === "success" || isImplicitSuccess
      ? "success"
      : "info";

  const config = {
    destructive: {
      icon: AlertCircle,
      badgeBg: "bg-rose-50 text-rose-600 border border-rose-200",
      progressBg: "bg-rose-500",
    },
    warning: {
      icon: AlertTriangle,
      badgeBg: "bg-amber-50 text-amber-700 border border-amber-200",
      progressBg: "bg-amber-500",
    },
    success: {
      icon: CheckCircle2,
      badgeBg: "bg-emerald-50 text-emerald-600 border border-emerald-200",
      progressBg: "bg-emerald-500",
    },
    info: {
      icon: Info,
      badgeBg: "bg-blue-50 text-blue-600 border border-blue-200",
      progressBg: "bg-blue-600",
    },
  }[effectiveVariant];

  const Icon = config.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94, y: 10 }}
      transition={{ type: "spring", stiffness: 440, damping: 30 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="pointer-events-auto relative w-full overflow-hidden rounded-xl bg-white border border-slate-200 p-3.5 shadow-lg transition-all duration-200 select-none group"
    >
      <div className="flex items-start gap-3">
        {/* Crisp SaaS Status Badge */}
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.badgeBg} shadow-2xs mt-0.5`}
        >
          <Icon className="h-4 w-4 shrink-0" />
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0 pr-5">
          {toast.title && (
            <h4 className="text-xs font-bold text-slate-900 tracking-tight leading-snug">
              {toast.title}
            </h4>
          )}
          {toast.description && (
            <p className="text-[11.5px] text-slate-600 leading-relaxed mt-0.5 break-words">
              {toast.description}
            </p>
          )}
          {toast.action && (
            <div className="mt-2 flex items-center gap-2">{toast.action}</div>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={() => onDismiss(toast.id)}
          className="absolute right-2.5 top-2.5 p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          title="Dismiss notification"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Subtle Auto-Dismiss Progress Line at Base */}
      <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-slate-100">
        <div
          className={`h-full ${config.progressBg} transition-all duration-75`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </motion.div>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useToast();

  // Active toasts (filtered to those with open !== false)
  const activeToasts = toasts.filter((t) => t.open !== false);

  if (activeToasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="fixed bottom-4 sm:bottom-6 right-3 sm:right-6 z-[99999] pointer-events-none flex flex-col gap-2.5 w-[calc(100vw-1.5rem)] sm:w-[380px] sm:max-w-[400px]"
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {/* Render ordered: newest notification at bottom */}
        {activeToasts
          .slice()
          .reverse()
          .map((t) => (
            <SaasToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
      </AnimatePresence>
    </div>
  );
}
